import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Trade() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, list: "trade" });
      toast.success("Welcome. Trade credentials will follow within 48 hours.");
      setEmail("");
      setSent(true);
    } catch (err) {
      toast.error("Couldn't submit. Please try again or email the studio.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="trade-page">
      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="label mb-3">Trade & Interior Designer Program</p>
            <h1 className="serif text-5xl md:text-7xl leading-[0.98] tracking-tight font-light">
              Original work for the rooms{" "}
              <span className="italic text-[hsl(var(--ink-soft))]">
                you&apos;re building.
              </span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-[hsl(var(--ink-soft))] leading-relaxed">
              A private program for interior designers, architects, hospitality
              buyers, and stagers sourcing at scale. Trade pricing, priority
              access, and installation guidance — direct from the studio.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-b border-border bg-[hsl(var(--surface))]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="label mb-8">What&apos;s included</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {BENEFITS.map((b, i) => (
              <div key={b.title} data-testid={`trade-benefit-${i}`}>
                <p className="serif text-5xl italic text-[hsl(var(--accent))] leading-none">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="serif text-2xl md:text-3xl mt-4 leading-tight">
                  {b.title}
                </h3>
                <p className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="label mb-3">Who it&apos;s for</p>
            <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Built for professional buyers, not casual browsers.
            </h2>
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-border">
              {AUDIENCES.map((line) => (
                <li
                  key={line}
                  className="py-4 flex items-start gap-4 serif text-lg md:text-xl"
                >
                  <span className="text-[hsl(var(--accent))] mt-1">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Apply */}
      <section
        className="bg-foreground text-background"
        data-testid="trade-apply"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="label !text-background/60 mb-4">Apply</p>
            <h2 className="serif text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Request trade credentials.
            </h2>
            <p className="mt-6 text-sm text-background/70 max-w-lg">
              Leave your business email and the studio will follow up within 48
              hours with a private lookbook, current inventory, trade pricing,
              and lead times.
            </p>
          </div>
          {sent ? (
            <div className="md:col-span-5" data-testid="trade-sent">
              <p className="label !text-background/60 mb-3">Received</p>
              <p className="serif text-3xl leading-tight">
                Thank you. The studio will be in touch within 48 hours.
              </p>
              <Link
                to="/work"
                className="mt-6 inline-flex items-center gap-2 label !text-background link-underline"
              >
                Browse the catalog <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="md:col-span-5"
              data-testid="trade-form"
            >
              <div className="flex border-b border-background/40 pb-2">
                <Mail className="h-5 w-5 self-center mr-3 opacity-70" />
                <input
                  type="email"
                  required
                  placeholder="you@studio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent outline-none placeholder:text-background/40 text-base py-3"
                  data-testid="trade-email-input"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-2 label !text-background"
                  data-testid="trade-submit"
                >
                  {busy ? "..." : "Apply"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-xs text-background/50 mt-3">
                Screened. Not a sales list. Never shared.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const BENEFITS = [
  {
    title: "Trade pricing on originals",
    body: "A confidential trade discount off the retail price on original acquisitions. Applied on a per-project basis for verified professionals.",
  },
  {
    title: "Priority access to new work",
    body: "See originals before they hit the public catalog. First refusal on pieces that fit projects you've told the studio about.",
  },
  {
    title: "Installation & specification support",
    body: "Dimensions, weight, hanging guidance, and framing options for spec sheets. Custom crating and white-glove shipping quoted per project.",
  },
];

const AUDIENCES = [
  "Interior designers sourcing statement work for private residences",
  "Architects and design-build studios furnishing new construction",
  "Hospitality buyers (hotels, restaurants, clubs, wellness spaces)",
  "Home stagers, real estate developers, and property stylists",
  "Corporate art programs and healthcare curators",
  "Set designers and film production art departments",
];
